// ============================================================================
// Clients-list pure helpers — no React, no side effects.
// ----------------------------------------------------------------------------
// Everything the roster + create form need to derive client-side:
//   • age from an ISO birthday
//   • sex normalization to the design-system's three codes
//   • a short, locale-aware date label
//   • the WEEKLY AVAILABILITY codec — a JSON convention stored in the free-text
//     `client.availability` column. Kept byte-compatible with the client-detail
//     editor (same "vfwk1" magic + shape) so a week set here round-trips there.
//   • per-client last / next session derivation from the trainer-wide calendar
//     feed (so the roster shows recency + an upcoming flag without N requests).
//   • the search + sort + filter selectors that drive the visible roster.
// All "optional-first": a missing input yields null / an empty result, never a
// throw and never a fake zero.
// ============================================================================
import type { CalendarSession, Client } from "../../api";
import { intlLocale } from "../../i18n/config";

// --- age --------------------------------------------------------------------

/** Whole years between an ISO birthday and today (null if missing/malformed). */
export function ageFromBirthday(
  birthday: string | null | undefined,
  today: Date = new Date(),
): number | null {
  if (!birthday) return null;
  const b = new Date(birthday);
  if (Number.isNaN(b.getTime())) return null;
  let age = today.getFullYear() - b.getFullYear();
  const m = today.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < b.getDate())) age -= 1;
  return age >= 0 ? age : null;
}

// --- sex --------------------------------------------------------------------

export type SexCode = "m" | "f" | "";

/** Normalize the loose backend sex string to one of our three codes. */
export function normalizeSex(sex: string | null | undefined): SexCode {
  const s = (sex ?? "").trim().toLowerCase();
  if (s === "m" || s === "male") return "m";
  if (s === "f" || s === "female") return "f";
  return "";
}

// --- activity level ---------------------------------------------------------

/**
 * The five activity-level keys, matching the backend `ACTIVITY_MULTIPLIERS`
 * (viccofitness/metrics.py: sedentary 1.2 → extra 1.9). Shared by the create +
 * edit forms; labels live in the `activity` i18n namespace. `""` means unset.
 */
export const ACTIVITY_LEVELS = [
  "sedentary",
  "light",
  "moderate",
  "very",
  "extra",
] as const;

export type ActivityLevelCode = (typeof ACTIVITY_LEVELS)[number] | "";

/** Normalize a loose backend activity string to one of our five codes (or ""). */
export function normalizeActivityLevel(
  value: string | null | undefined,
): ActivityLevelCode {
  const v = (value ?? "").trim().toLowerCase();
  return (ACTIVITY_LEVELS as readonly string[]).includes(v)
    ? (v as ActivityLevelCode)
    : "";
}

// --- date formatting --------------------------------------------------------

/** A short, locale-aware date label, e.g. "16 Jun 2026". null-safe. */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(intlLocale(), {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** Whole-day delta (b − a), in calendar days. */
function daysBetween(a: Date, b: Date): number {
  return Math.round(
    (startOfDay(b).getTime() - startOfDay(a).getTime()) / 86_400_000,
  );
}

// ============================================================================
// WEEKLY AVAILABILITY codec
// ----------------------------------------------------------------------------
// Byte-compatible with pages/client-detail/lib.ts: a single free-text column
// holds either our JSON ({v:"vfwk1", days:{…}}) or legacy plain text. The
// create form only ever writes the JSON; legacy text only matters for editing
// an existing record, which the detail page owns — so here we just parse what
// we need and always serialize forward.
// ============================================================================

export const DAY_KEYS = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
] as const;
export type DayKey = (typeof DAY_KEYS)[number];

export interface DayAvailability {
  on: boolean;
  from: string; // "HH:MM"
  to: string; // "HH:MM"
}

export type WeeklyAvailability = Record<DayKey, DayAvailability>;

const MAGIC = "vfwk1"; // version tag — matches the client-detail codec.

export function emptyAvailability(): WeeklyAvailability {
  return DAY_KEYS.reduce((acc, d) => {
    acc[d] = { on: false, from: "09:00", to: "17:00" };
    return acc;
  }, {} as WeeklyAvailability);
}

function isHHMM(s: unknown): s is string {
  return typeof s === "string" && /^\d{2}:\d{2}$/.test(s);
}

/** True if at least one day is marked available. */
export function hasAnyAvailability(weekly: WeeklyAvailability): boolean {
  return DAY_KEYS.some((d) => weekly[d].on);
}

export interface ParsedAvailability {
  weekly: WeeklyAvailability;
  /** Non-null when the stored value was legacy free text (not our JSON). */
  legacyText: string | null;
}

/** Parse a stored availability string into a structured week (+ legacy text). */
export function parseAvailability(
  raw: string | null | undefined,
): ParsedAvailability {
  const weekly = emptyAvailability();
  if (!raw || !raw.trim()) return { weekly, legacyText: null };
  try {
    const obj = JSON.parse(raw) as unknown;
    if (
      obj &&
      typeof obj === "object" &&
      (obj as { v?: string }).v === MAGIC &&
      (obj as { days?: unknown }).days &&
      typeof (obj as { days: unknown }).days === "object"
    ) {
      const days = (obj as { days: Record<string, unknown> }).days;
      for (const d of DAY_KEYS) {
        const entry = days[d];
        if (entry && typeof entry === "object") {
          const e = entry as Partial<DayAvailability>;
          weekly[d] = {
            on: Boolean(e.on),
            from: isHHMM(e.from) ? e.from : "09:00",
            to: isHHMM(e.to) ? e.to : "17:00",
          };
        }
      }
      return { weekly, legacyText: null };
    }
  } catch {
    // not our JSON — treat as legacy free text
  }
  return { weekly, legacyText: raw.trim() };
}

/** Serialize a structured week back to the stored string ("" when all off). */
export function serializeAvailability(weekly: WeeklyAvailability): string {
  if (!hasAnyAvailability(weekly)) return "";
  return JSON.stringify({ v: MAGIC, days: weekly });
}

// ============================================================================
// SESSION recency — derive last completed + next planned per client.
// ----------------------------------------------------------------------------
// Mirrors the dashboard's derivation so the roster's "last session" / follow-up
// language matches the rest of the app. Sourced once from the trainer-wide
// calendar feed (GET /api/calendar) and bucketed client-side.
// ============================================================================

export const FOLLOW_UP_DAYS = 7;

export interface ClientActivity {
  /** ISO datetime of the most recent COMPLETED session (null if none). */
  lastIso: string | null;
  /** Whole days since that session (null if none). */
  daysSinceLast: number | null;
  /** ISO datetime of the next PLANNED session from today (null if none). */
  nextIso: string | null;
  /** True when active + no upcoming session + stale/never-trained. */
  needsFollowUp: boolean;
}

function sessionStart(s: CalendarSession): string | null {
  return s.performed_at ?? s.scheduled_at ?? null;
}

/**
 * Bucket the whole calendar feed into per-client activity. One pass; returns a
 * Map keyed by client id. Archived clients never flag as follow-up.
 */
export function buildActivity(
  clients: Client[],
  sessions: CalendarSession[],
  now: Date = new Date(),
): Map<number, ClientActivity> {
  const today0 = startOfDay(now);
  const nowMs = now.getTime();

  const lastByClient = new Map<number, string>();
  const nextByClient = new Map<number, string>();

  for (const s of sessions) {
    const start = sessionStart(s);
    if (!start) continue;
    const ms = new Date(start).getTime();
    if (Number.isNaN(ms)) continue;

    if (s.status === "completed") {
      const prev = lastByClient.get(s.client_id);
      if (!prev || start > prev) lastByClient.set(s.client_id, start);
    } else if (s.status === "planned" && ms >= nowMs) {
      const prev = nextByClient.get(s.client_id);
      if (!prev || start < prev) nextByClient.set(s.client_id, start);
    }
  }

  const out = new Map<number, ClientActivity>();
  for (const c of clients) {
    const lastIso = lastByClient.get(c.id) ?? null;
    const nextIso = nextByClient.get(c.id) ?? null;
    const daysSinceLast =
      lastIso != null ? daysBetween(new Date(lastIso), today0) : null;
    const stale = daysSinceLast == null || daysSinceLast > FOLLOW_UP_DAYS;
    const needsFollowUp =
      c.status !== "archived" && nextIso == null && stale;
    out.set(c.id, { lastIso, daysSinceLast, nextIso, needsFollowUp });
  }
  return out;
}

// --- filtering / sorting ----------------------------------------------------

export type ClientFilter = "all" | "favorites" | "followUp" | "archived";

export interface ClientRow {
  client: Client;
  activity: ClientActivity;
}

const EMPTY_ACTIVITY: ClientActivity = {
  lastIso: null,
  daysSinceLast: null,
  nextIso: null,
  needsFollowUp: false,
};

/** Case/diacritic-insensitive substring match on the client name. */
function matchesQuery(name: string, q: string): boolean {
  if (!q) return true;
  return name.toLocaleLowerCase().includes(q.toLocaleLowerCase());
}

/**
 * The visible roster: join clients with their derived activity, apply the
 * active filter + search query, and sort. Default sort surfaces follow-ups
 * first (most overdue at the top), then by recency, then alphabetically — so
 * the screen leads with who needs the trainer.
 */
export function selectRoster(
  clients: Client[],
  activity: Map<number, ClientActivity>,
  filter: ClientFilter,
  query: string,
): ClientRow[] {
  const q = query.trim();
  const rows: ClientRow[] = clients
    .filter((c) => {
      if (!matchesQuery(c.name, q)) return false;
      switch (filter) {
        case "favorites":
          return c.starred === 1;
        case "followUp":
          return (activity.get(c.id) ?? EMPTY_ACTIVITY).needsFollowUp;
        case "archived":
          return c.status === "archived";
        case "all":
        default:
          return c.status !== "archived";
      }
    })
    .map((client) => ({
      client,
      activity: activity.get(client.id) ?? EMPTY_ACTIVITY,
    }));

  rows.sort((a, b) => {
    // 1) follow-ups bubble up, most overdue first
    const aN = a.activity.needsFollowUp ? 1 : 0;
    const bN = b.activity.needsFollowUp ? 1 : 0;
    if (aN !== bN) return bN - aN;
    if (aN === 1) {
      const ad = a.activity.daysSinceLast ?? Number.POSITIVE_INFINITY;
      const bd = b.activity.daysSinceLast ?? Number.POSITIVE_INFINITY;
      if (ad !== bd) return bd - ad;
    }
    // 2) most-recently active first (never-trained sinks)
    const al = a.activity.lastIso ?? "";
    const bl = b.activity.lastIso ?? "";
    if (al !== bl) return bl.localeCompare(al);
    // 3) stable alphabetical
    return a.client.name.localeCompare(b.client.name);
  });

  return rows;
}

/** Counts for the filter tabs (computed off the full client + activity set). */
export interface FilterCounts {
  all: number;
  favorites: number;
  followUp: number;
  archived: number;
}

export function countByFilter(
  clients: Client[],
  activity: Map<number, ClientActivity>,
): FilterCounts {
  let all = 0;
  let favorites = 0;
  let followUp = 0;
  let archived = 0;
  for (const c of clients) {
    if (c.status === "archived") archived += 1;
    else all += 1;
    if (c.starred === 1) favorites += 1;
    if ((activity.get(c.id) ?? EMPTY_ACTIVITY).needsFollowUp) followUp += 1;
  }
  return { all, favorites, followUp, archived };
}
